import {
  Button,
  Card,
  CardBody,
  HStack,
  LinkBox,
  LinkOverlay,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from "@chakra-ui/react";
import AddFundFamilyModal from "features/fund-family/AddFundFamilyModal";
import { FundFamilyStateBadge } from "features/fund-family/FundFamilyStateBadge";
import { useFundFamilyEnrichedListSuspense } from "gen/endpoints/fund-family-enriched/fund-family-enriched";
import { FundFamilyEnrichedData } from "gen/model/fundFamilyEnrichedData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContainer from "shared/components/layout/PageContainer";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { RouteLink } from "shared/components/link/RouteLink";
import { useToast } from "shared/components/toast/useToast";
import useDocumentTitle from "shared/hooks/useDocumentTitle";
import {
  ElementsMinWidthProvider,
  useElementsMinWidth,
} from "shared/hooks/useElementsMinWidth";
import { rootRoute } from "shared/navigation/Routes";
import { formatDateRange } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { useBoolean } from "usehooks-ts";

export const FundFamilyListPage: React.FC = () => {
  const { showToast } = useToast();
  const { t } = useTranslation(["common", "fundFamily"]);

  useDocumentTitle(t("fundFamily:fundFamilies"));

  const {
    value: isAddDialogOpen,
    setTrue: openAddDialog,
    setFalse: closeAddDialog,
  } = useBoolean();

  const { data, refetch } = useFundFamilyEnrichedListSuspense({});

  const addFundFamilyRow = useCallback(async () => {
    await refetch();
    showToast({
      message: t("fundFamily:add.success"),
      variant: "success",
    });
  }, [showToast, refetch, t]);

  return (
    <PageContainer>
      <PageContent isFullHeight={true}>
        <PageHeader
          title={t("fundFamily:fundFamilies")}
          size="lg"
          actions={
            <Restricted permission={PermissionTypeEnum.config}>
              <Button onClick={openAddDialog} size="xs">
                {t("fundFamily:createFundFamily")}
              </Button>
            </Restricted>
          }
        />
        <PageBody>
          <VStack spacing="16px" mb="16px" alignItems="stretch">
            <ElementsMinWidthProvider>
              {data.map((fundFamily) => (
                <FundFamilyCard key={fundFamily.id} fundFamily={fundFamily} />
              ))}
            </ElementsMinWidthProvider>
          </VStack>
          {isAddDialogOpen && (
            <AddFundFamilyModal
              onSuccess={addFundFamilyRow}
              onCloseComplete={closeAddDialog}
            />
          )}
        </PageBody>
      </PageContent>
    </PageContainer>
  );
};

interface FundFamilyCardProps {
  fundFamily: FundFamilyEnrichedData;
}

const FundFamilyCard: React.FC<FundFamilyCardProps> = ({ fundFamily }) => {
  const { t } = useTranslation(["fundFamily"]);
  const { ref, minWidth } = useElementsMinWidth();
  return (
    <LinkBox>
      <Card variant="clickable">
        <CardBody>
          <HStack columnGap="80px" flexWrap="wrap" minHeight="72px">
            <VStack
              alignItems="stretch"
              gap="8px"
              ref={ref}
              minWidth={minWidth}
            >
              <HStack gap="8px">
                <LinkOverlay
                  as={RouteLink}
                  textStyle="h5"
                  zIndex={1}
                  to={
                    rootRoute({})
                      .fundFamilies({})
                      .fundFamily({ fundFamilyId: fundFamily.id }).$
                  }
                >
                  {fundFamily.name}
                </LinkOverlay>
                <FundFamilyStateBadge state={fundFamily.state} />
              </HStack>
              <Text textStyle="small">
                {formatDateRange(
                  fundFamily.dates?.fund_start_date,
                  fundFamily.dates?.fund_end_date,
                  null,
                )}
              </Text>
            </VStack>
            <StatGroup columnGap="56px !important">
              <FundFamilyStat
                label={t("fundFamily:totalCommitment")}
                value={formatMoney(
                  fundFamily.commitment_stats?.total_commitment_amount,
                )}
              />
              <FundFamilyStat
                label={t("fundFamily:totalCalled")}
                value={formatMoney(
                  fundFamily.commitment_stats?.total_called_amount,
                )}
              />
              <FundFamilyStat
                label={t("fundFamily:totalDistributed")}
                value={formatMoney(
                  fundFamily.commitment_stats?.total_distributed_amount,
                )}
              />
              <FundFamilyStat
                label={t("fundFamily:totalCarryPaid")}
                value={formatMoney(
                  fundFamily.commitment_stats?.total_carry_amount,
                )}
              />
            </StatGroup>
          </HStack>
        </CardBody>
      </Card>
    </LinkBox>
  );
};

const FundFamilyStat: React.FC<{
  label: string;
  value: string | null | undefined;
}> = ({ label, value }) =>
  value ? (
    <Stat size="xxs" py="0">
      <StatLabel>{label}</StatLabel>
      <StatNumber>{value}</StatNumber>
    </Stat>
  ) : null;
